import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import TextFieldsRoundedIcon from "@mui/icons-material/TextFieldsRounded";
import PaletteRoundedIcon from "@mui/icons-material/PaletteRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { BRAND } from "./config";
import { findBuilderStep, getSiteBuilderProgress, SITE_SECTION_META } from "./siteBuilderModel";

function actionIcon(action) {
  if (action.type === "section") return <TextFieldsRoundedIcon />;
  if (action.tab === 0) return <PaletteRoundedIcon />;
  if (action.tab === 1) return <ImageRoundedIcon />;
  if (action.tab === 2) return <VisibilityRoundedIcon />;
  if (action.tab === 4) return <RocketLaunchRoundedIcon />;
  return <TuneRoundedIcon />;
}

function actionIsComplete(action, form, step) {
  if (action.section === "identity") {
    return Boolean(String(form.business?.name || "").trim() && String(form.business?.shortDescription || "").trim());
  }
  if (action.section === "navbar") return form.sections?.showNavbar !== false;
  if (action.section === "hero") return Boolean(String(form.hero?.title || "").trim() && String(form.hero?.subtitle || "").trim());
  if (action.section === "featured") return Boolean(String(form.catalog?.featured?.title || "").trim());
  if (action.section === "categories") return Boolean(String(form.catalog?.categories?.title || "").trim());
  if (action.section === "brands") {
    return Array.isArray(form.catalog?.brands?.items) &&
      form.catalog.brands.items.some((item) => String(item?.name || item?.logoUrl || "").trim());
  }
  if (action.section === "spotlight") return form.catalog?.spotlight?.enabled === true && Boolean(String(form.catalog?.spotlight?.title || "").trim());
  if (action.section === "about") return Boolean(String(form.about?.title || "").trim() && String(form.about?.description || "").trim());
  if (action.section === "highlights") {
    return Array.isArray(form.highlights) && form.highlights.filter((item) => String(item?.title || "").trim()).length >= 2;
  }
  if (action.section === "contact") return Boolean(String(form.contact?.title || "").trim());
  if (action.section === "floatingWhatsapp") return Boolean(String(form.contact?.whatsappMessage || "").trim());
  if (action.section === "footer") return form.sections?.showFooter !== false;
  if (action.tab === 0) return Boolean(form.theme?.primaryColor && form.theme?.secondaryColor);
  if (action.tab === 1 && step.id === "identity") return Boolean(form.theme?.logoUrl);
  if (action.tab === 1 && step.id === "cover") {
    return Boolean(
      form.hero?.imageUrl ||
        (Array.isArray(form.hero?.slides) && form.hero.slides.some((slide) => String(slide?.url || "").trim()))
    );
  }
  if (action.tab === 1 && step.id === "trust") return Boolean(form.about?.imageUrl);
  if (action.tab === 2) return true;
  if (action.tab === 3) {
    return Boolean(form.business?.whatsapp || form.business?.phone || form.business?.email);
  }
  if (action.tab === 4) return form.publicStatus === "published";
  return false;
}

function ColorMeaning({ color, label, usage }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: 1.5,
          bgcolor: color,
          border: "1px solid rgba(25,36,47,.14)",
          boxShadow: `0 0 0 4px ${color}18`,
          flex: "0 0 auto",
        }}
      />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ display: "block", fontWeight: 950, color: BRAND.dark }}>
          {label}
        </Typography>
        <Typography variant="caption" sx={{ display: "block", color: "rgba(25,36,47,.55)", lineHeight: 1.25 }}>
          {usage}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function LandingControlCenter({
  form,
  productsEnabled,
  activeStep,
  onOpenSection,
  onOpenSettings,
  onSelectStep,
}) {
  const progress = React.useMemo(
    () => getSiteBuilderProgress(form, productsEnabled),
    [form, productsEnabled]
  );
  const step = findBuilderStep(activeStep, productsEnabled);
  const progressStep = progress.steps.find((item) => item.id === step.id) || progress.steps[0];
  const nextStep = progress.steps.find((item) => !item.done && item.id !== step.id) || null;
  const pendingEssential = progressStep.checks.filter((check) => check.required !== false && !check.done);

  return (
    <Stack spacing={2}>
      <Card
        elevation={0}
        sx={{
          borderRadius: 4,
          border: "1px solid rgba(25,36,47,.10)",
          overflow: "hidden",
          boxShadow: "0 18px 50px rgba(25,36,47,.07)",
        }}
      >
        <Box
          sx={{
            p: { xs: 2, md: 2.5 },
            color: "#fff",
            background: `linear-gradient(135deg, ${BRAND.dark} 0%, ${form.theme?.secondaryColor || BRAND.dark} 100%)`,
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.5}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="overline" sx={{ color: form.theme?.primaryColor || BRAND.orange, fontWeight: 950, letterSpacing: 1 }}>
                PASO {progressStep.number} DE {progress.steps.length}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 950, lineHeight: 1.12 }}>
                {progressStep.label}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.65, maxWidth: 720, color: "rgba(255,255,255,.72)" }}>
                {progressStep.description}
              </Typography>
            </Box>
            <Chip
              label={progressStep.done ? "ESENCIAL COMPLETO" : `${progressStep.completed}/${progressStep.total} LISTOS`}
              icon={progressStep.done ? <CheckCircleRoundedIcon /> : undefined}
              sx={{
                alignSelf: { xs: "flex-start", sm: "center" },
                bgcolor: progressStep.done ? "rgba(82,200,120,.18)" : "rgba(255,255,255,.10)",
                color: "#fff",
                fontWeight: 950,
                "& .MuiChip-icon": { color: "#52c878" },
              }}
            />
          </Stack>
          <LinearProgress
            variant="determinate"
            value={progressStep.percentage}
            sx={{
              mt: 2,
              height: 7,
              borderRadius: 999,
              bgcolor: "rgba(255,255,255,.12)",
              "& .MuiLinearProgress-bar": {
                bgcolor: progressStep.done ? "#52c878" : form.theme?.primaryColor || BRAND.orange,
                borderRadius: 999,
              },
            }}
          />
        </Box>

        <Box sx={{ p: { xs: 2, md: 2.5 }, bgcolor: "#fff" }}>
          {pendingEssential.length ? (
            <Alert
              severity="info"
              icon={<AutoAwesomeRoundedIcon />}
              sx={{ mb: 2, borderRadius: 3, "& .MuiAlert-message": { width: "100%" } }}
            >
              <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Para cerrar este paso</Typography>
              <Typography variant="body2" sx={{ mt: 0.2 }}>
                {pendingEssential.map((item) => item.label).join(" · ")}
              </Typography>
            </Alert>
          ) : (
            <Alert severity="success" sx={{ mb: 2, borderRadius: 3 }}>
              Este paso ya está completo. Podés mejorarlo o avanzar al siguiente.
            </Alert>
          )}

          <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Elegí qué querés editar</Typography>
          <Typography variant="body2" sx={{ mt: 0.35, mb: 1.7, color: "rgba(25,36,47,.58)" }}>
            No hay una maqueta paralela: cada botón abre exactamente los controles de esa parte.
          </Typography>

          <Grid container spacing={1.4}>
            {progressStep.actions.map((action, index) => {
              const complete = actionIsComplete(action, form, progressStep);
              const pulse = !progressStep.done && index === 0;
              const sectionMeta = action.section ? SITE_SECTION_META[action.section] : null;
              return (
                <Grid item xs={12} md={6} key={action.id}>
                  <Button
                    type="button"
                    fullWidth
                    onClick={() => {
                      if (action.type === "section") onOpenSection(action.section);
                      else onOpenSettings(action.tab, progressStep.id);
                    }}
                    sx={{
                      height: "100%",
                      minHeight: 112,
                      p: 1.6,
                      borderRadius: 3,
                      border: `1px solid ${complete ? "rgba(22,131,75,.22)" : "rgba(25,36,47,.12)"}`,
                      justifyContent: "flex-start",
                      textAlign: "left",
                      textTransform: "none",
                      color: BRAND.dark,
                      bgcolor: complete ? "rgba(22,131,75,.035)" : "#fff",
                      boxShadow: "none",
                      position: "relative",
                      overflow: "hidden",
                      "&:hover": {
                        bgcolor: complete ? "rgba(22,131,75,.065)" : "rgba(243,90,58,.045)",
                        borderColor: complete ? "rgba(22,131,75,.35)" : "rgba(243,90,58,.38)",
                        boxShadow: "none",
                      },
                      ...(pulse
                        ? {
                            "@keyframes actionGlow": {
                              "0%, 100%": { boxShadow: "0 0 0 0 rgba(243,90,58,.10)" },
                              "50%": { boxShadow: "0 0 0 7px rgba(243,90,58,0)" },
                            },
                            animation: "actionGlow 1.8s ease-in-out infinite",
                          }
                        : {}),
                    }}
                  >
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        flex: "0 0 auto",
                        display: "grid",
                        placeItems: "center",
                        borderRadius: 2.3,
                        bgcolor: complete ? "rgba(22,131,75,.10)" : `${form.theme?.primaryColor || BRAND.orange}16`,
                        color: complete ? "#16834b" : form.theme?.primaryColor || BRAND.orange,
                      }}
                    >
                      {complete ? <CheckCircleRoundedIcon /> : actionIcon(action)}
                    </Box>
                    <Box sx={{ ml: 1.25, minWidth: 0, flex: 1 }}>
                      <Stack direction="row" justifyContent="space-between" spacing={1}>
                        <Typography sx={{ fontWeight: 950, lineHeight: 1.2 }}>
                          {action.label}
                        </Typography>
                        <ChevronRightRoundedIcon sx={{ color: "rgba(25,36,47,.38)", flex: "0 0 auto" }} />
                      </Stack>
                      <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "rgba(25,36,47,.57)", lineHeight: 1.35 }}>
                        {action.description || sectionMeta?.description}
                      </Typography>
                      <Typography variant="caption" sx={{ display: "block", mt: 0.7, fontWeight: 900, color: complete ? "#16834b" : BRAND.orange }}>
                        {complete ? "Configurado" : "Abrir ajustes"}
                      </Typography>
                    </Box>
                  </Button>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Card>

      <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,.10)", p: { xs: 2, md: 2.4 } }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }} justifyContent="space-between">
          <Box>
            <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Así se usan tus colores</Typography>
            <Typography variant="body2" sx={{ mt: 0.35, color: "rgba(25,36,47,.57)" }}>
              Cada muestra usa los valores actuales de la landing. Cambiarlos modifica esos elementos en toda la web.
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ minWidth: { md: 470 } }}>
            <ColorMeaning color={form.theme?.primaryColor || BRAND.orange} label="Principal" usage="Botones, enlaces y detalles" />
            <ColorMeaning color={form.theme?.secondaryColor || BRAND.dark} label="Secundario" usage="Navbar, footer y títulos fuertes" />
            <ColorMeaning color={form.theme?.textColor || BRAND.dark} label="Texto" usage="Párrafos y lectura general" />
          </Stack>
        </Stack>
      </Card>

      <Divider />

      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.2} alignItems={{ sm: "center" }}>
        <Typography variant="body2" sx={{ color: "rgba(25,36,47,.58)" }}>
          {progress.complete
            ? "La web tiene todo lo esencial. Las mejoras pendientes son opcionales."
            : `Progreso general: ${progress.completed} de ${progress.total} puntos esenciales.`}
        </Typography>
        {nextStep ? (
          <Button
            type="button"
            variant="outlined"
            endIcon={<ChevronRightRoundedIcon />}
            onClick={() => onSelectStep(nextStep.id)}
            sx={{ borderRadius: 999, textTransform: "none", fontWeight: 950, color: BRAND.dark, borderColor: "rgba(25,36,47,.18)" }}
          >
            Ir a {nextStep.shortLabel.toLowerCase()}
          </Button>
        ) : null}
      </Stack>
    </Stack>
  );
}
